/* eslint-disable react/jsx-props-no-spreading */
import type { Button } from '@coop/components';
import classNames from 'classnames';
import * as React from 'react';
import { useCallback } from 'react';

import usePromotionTracking from '../../../tracking/hooks/usePromotionTracking';
import type { IGaPromoData } from '../../../tracking/models/IGaPromoData';
import RawHtml from '../../atoms/RawHtml';
import RawPicture from '../../atoms/RawPicture';
import type Heading from '../../Heading';
import styles from './ImageTextPropertyBlock.module.scss';

export interface ImageTextPropertyBlockProps extends IGaPromoData {
    imageUrl?: string;
    imageHeight?: number;
    srcSet?: { srcSet: string; height?: number; minWidth: number }[];
    imageAltText?: string;

    centerBlockVertically: boolean;
    centerBlockHorizontally: boolean;
    contentLessPadding: boolean;
    contentLessHorizontalPadding: boolean;
    backgroundColor?:
        | 'u-bgWhite'
        | 'u-bgGreenDark'
        | 'u-bgGreen'
        | 'u-bgGreenLight2'
        | 'u-bgGrayLight';
    linkUrl?: string;
    target: '_self' | '_blank';
    isClickable: boolean;
    body?: string;
    textColor?: string;
    lazy?: boolean;
    ariaLabel: string;
    buttonProps?: React.ComponentPropsWithoutRef<typeof Button>;
    renderButton?: (props: React.ComponentPropsWithoutRef<typeof Button>) => React.ReactNode;
    secondaryButtonProps?: React.ComponentPropsWithoutRef<typeof Button>;
    renderSecondaryButton?: (
        props: React.ComponentPropsWithoutRef<typeof Button>,
    ) => React.ReactNode;
    headingProps?: React.ComponentPropsWithoutRef<typeof Heading>;
    renderHeading?: (props: React.ComponentPropsWithoutRef<typeof Heading>) => React.ReactNode;
}

const ImageTextPropertyBlock = (props: ImageTextPropertyBlockProps) => {
    const { ref: trackingRef, click: trackClick } = usePromotionTracking({
        gaPromoCreative: props.gaPromoCreative,
        gaPromoId: props.gaPromoId,
        gaPromoName: props.gaPromoName,
        gaPromoPosition: props.gaPromoPosition,
    });

    const ref = useCallback(
        (node: HTMLElement | null) => {
            trackingRef(node);
        },
        [trackingRef],
    );

    const anchorRel = props.target?.includes('blank') ? 'noopener noreferrer' : undefined;

    // If whole banner is clickable, render is as anchor and dont render the button inside to not have nested interactive elements
    if (props.isClickable) {
        return (
            <a
                ref={ref}
                className={classNames(styles.Container, styles.LinkWrapper, props.backgroundColor)}
                href={props.linkUrl}
                target={props.target}
                rel={anchorRel}
                onClick={trackClick}
                aria-label={props.ariaLabel}
            >
                <Content {...props} />
            </a>
        );
    }
    return (
        <div ref={ref} className={classNames(styles.Container, props.backgroundColor)}>
            <Content
                {...props}
                belowTextSlot={
                    !!props.buttonProps &&
                    !!props.renderButton && (
                        <BottomSlot
                            onClick={trackClick}
                            centerBlockHorizontally={props.centerBlockHorizontally}
                            centerBlockVertically={props.centerBlockVertically}
                            buttonProps={props.buttonProps}
                            renderButton={props.renderButton}
                            secondaryButtonProps={props.secondaryButtonProps}
                            renderSecondaryButton={props.renderSecondaryButton}
                        />
                    )
                }
            />
        </div>
    );
};

const Content = (props: ImageTextPropertyBlockProps & { belowTextSlot?: React.ReactNode }) => {
    const bannerPositionClassNames = classNames(
        styles.Content,
        props.centerBlockHorizontally && styles['Content--horizontalCenter'],
        props.centerBlockVertically
            ? styles['Content--verticalCenter']
            : styles['Content--noVerticalCenter'],
        props.contentLessPadding && styles['Content--contentLessPadding'],
        props.contentLessHorizontalPadding && styles['Content--contentLessHorizontalPadding'],
    );

    const hasContent =
        !!props.body ||
        React.isValidElement(props.belowTextSlot) ||
        (!!props.headingProps &&
            !!props.renderHeading &&
            React.isValidElement(props.renderHeading(props.headingProps)));

    return (
        <>
            {(props.imageUrl || !!props.srcSet?.length) && (
                <RawPicture
                    altText={props.imageAltText}
                    srcSet={props.srcSet}
                    src={props.imageUrl}
                    height={props.imageHeight}
                    imgClassName={classNames(
                        styles.Image,
                        !props.imageHeight && styles['Image--noExplicitHeight'],
                    )}
                    className={classNames(
                        styles.ImageContainer,
                        hasContent && styles['ImageContainer--absolute'], // if there is any content, it should dictate the height of the block, so make image absolute to be positioned behind the content
                        !hasContent && styles['ImageContainer--relative'], // no content - image is dictating the height of the block
                    )}
                    lazy={props.lazy}
                />
            )}
            {hasContent && (
                <div className={bannerPositionClassNames}>
                    <div className="u-sizeFull">
                        {!!props.headingProps &&
                            !!props.renderHeading &&
                            props.renderHeading({
                                ...props.headingProps,
                                fontFamilySecondary: true,
                                alignment: props.centerBlockHorizontally
                                    ? 'center'
                                    : props.headingProps.alignment,
                                className: classNames(
                                    props.headingProps.className,
                                    styles.Heading,
                                    'u-marginBsm',
                                ),
                            })}
                        {props.body && (
                            <div className={classNames('Editorial', props.textColor)}>
                                <RawHtml html={props.body} />
                            </div>
                        )}
                    </div>

                    {props.belowTextSlot}
                </div>
            )}
        </>
    );
};

const BottomSlot = (props: {
    centerBlockHorizontally: boolean;
    centerBlockVertically: boolean;
    onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
    buttonProps: React.ComponentPropsWithoutRef<typeof Button>;
    renderButton: (props: React.ComponentPropsWithoutRef<typeof Button>) => React.ReactNode;
    secondaryButtonProps?: React.ComponentPropsWithoutRef<typeof Button>;
    renderSecondaryButton?: (
        props: React.ComponentPropsWithoutRef<typeof Button>,
    ) => React.ReactNode;
}) => {
    const bothButtonsExist = props.buttonProps && props.secondaryButtonProps;

    const wrapperClassNames = classNames(
        styles.ButtonWrapper,
        bothButtonsExist && props.centerBlockHorizontally && 'u-flexJustifyCenter',
        props.buttonProps.fullWidth && 'u-size1of1',
    );

    return (
        <div className={wrapperClassNames}>
            {props.renderButton({
                ...props.buttonProps,
                style:
                    bothButtonsExist && props.centerBlockHorizontally
                        ? {
                              ...props.buttonProps.style,
                              // when two buttons exist and are centered then they will be aligned not by themselves, but by the parent component
                              margin: 0,
                          }
                        : props.buttonProps.style,
                onClick: (e) => {
                    props.onClick?.(e);
                    props.buttonProps?.onClick?.(e);
                },
            })}
            {/* Secondary is rendered only if primary already exists */}
            {!!props.buttonProps &&
                !!props.renderButton &&
                !!props.secondaryButtonProps &&
                props.renderSecondaryButton?.({
                    ...props.secondaryButtonProps,
                    style:
                        bothButtonsExist && props.centerBlockHorizontally
                            ? {
                                  ...props.secondaryButtonProps.style,
                                  // when two buttons exist and are centered then they will be aligned not by themselves, but by the parent component
                                  margin: 0,
                              }
                            : props.secondaryButtonProps.style,
                    onClick: (e) => {
                        props.onClick?.(e);
                        props.secondaryButtonProps?.onClick?.(e);
                    },
                })}
        </div>
    );
};

export default ImageTextPropertyBlock;
